import React, { useEffect } from "react"
import { Switch, Route } from "react-router-dom"
import { connect } from "react-redux"
import * as actions from "./store/actions"
import axios from "axios"
import { createTheme, StylesProvider } from "@material-ui/core/styles"
import { ThemeProvider } from "@material-ui/styles"
import ReactGA from "react-ga"

import Footer from "./ui/Footer"

import Home from "./pages/Home"
import Collection from "./pages/Collection"
import Product from "./pages/Product"
import Basket from "./pages/Basket"
import Contacts from "./pages/Contacts"
import Faq from "./pages/Faq"
import AboutUs from "./pages/AboutUs"
import Support from "./pages/Support"
import Promotions from "./pages/Promotions"
import Feedbacks from "./pages/Feedbacks"
import PublicOfferAgreement from "./pages/PublicOfferAgreement"
import PrivacyPolicy from "./pages/PrivacyPolicy"

const displayFont = '"Unbounded", "Onest", system-ui, sans-serif'
const textFont = '"Onest", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'

const theme = createTheme({
  palette: {
    type: "light",
    primary: {
      light: "#3f7d2a",
      main: "#165b00",
      dark: "#0f4200",
      contrastText: "#ffffff"
    },
    secondary: {
      main: "#b3261e"
    },
    error: {
      main: "#b3261e"
    },
    text: {
      primary: "#1c2319",
      secondary: "#5a6555"
    },
    divider: "#dde5d8",
    background: {
      default: "#ffffff",
      paper: "#ffffff"
    }
  },
  shape: {
    borderRadius: 10
  },
  typography: {
    fontFamily: textFont,
    h1: { fontFamily: displayFont, fontWeight: 500 },
    h2: { fontFamily: displayFont, fontWeight: 500 },
    h3: { fontFamily: displayFont, fontWeight: 500 },
    h4: { fontFamily: displayFont, fontWeight: 500 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600, fontSize: "1.125rem" },
    button: {
      textTransform: "none",
      fontWeight: 600,
      letterSpacing: 0
    }
  },
  props: {
    MuiButton: {
      disableElevation: true
    },
    MuiTextField: {
      variant: "outlined"
    },
    MuiSelect: {
      variant: "outlined"
    }
  },
  overrides: {
    MuiButton: {
      root: {
        borderRadius: 999,
        padding: "8px 20px",
        fontSize: "0.9375rem"
      },
      sizeLarge: {
        padding: "12px 28px",
        fontSize: "1rem"
      },
      outlined: {
        padding: "7px 19px"
      },
      text: {
        padding: "8px 14px"
      }
    },
    MuiIconButton: {
      root: {
        color: "#1c2319"
      }
    },
    MuiDialog: {
      paper: {
        borderRadius: 20
      }
    },
    MuiDialogTitle: {
      root: {
        padding: "22px 24px 10px",
        "& .MuiTypography-root": {
          fontFamily: displayFont,
          fontWeight: 500,
          fontSize: "1.25rem",
          lineHeight: 1.3
        }
      }
    },
    MuiDialogContent: {
      root: {
        padding: "8px 24px"
      }
    },
    MuiDialogActions: {
      root: {
        padding: "16px 24px 22px",
        flexWrap: "wrap",
        gap: "8px"
      },
      spacing: {
        "& > :not(:first-child)": {
          marginLeft: 0
        }
      }
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: 10,
        backgroundColor: "#ffffff"
      }
    },
    MuiPaper: {
      rounded: {
        borderRadius: 14
      }
    },
    MuiMenuItem: {
      root: {
        fontSize: "0.9375rem",
        minHeight: 44
      }
    },
    MuiSnackbarContent: {
      root: {
        backgroundColor: "#0c2e06",
        borderRadius: 12,
        fontSize: "0.9375rem"
      }
    },
    MuiTooltip: {
      tooltip: {
        backgroundColor: "#0c2e06",
        fontSize: "0.8125rem",
        borderRadius: 8,
        padding: "8px 12px",
        maxWidth: 260
      }
    },
    MuiBadge: {
      badge: {
        fontWeight: 700
      }
    }
  }
})

const App = ({ applySession }) => {
  axios.defaults.baseURL = process.env.REACT_APP_API

  useEffect(() => {
    ReactGA.initialize('UA-188364243-1')
    ReactGA.pageview(window.location.pathname + window.location.search);
  }, [])

  useEffect(() => {
    axios(`/session/`, {
      withCredentials: true
    })
      .then(res => {
        console.log("[APP::LAUNCH]: ", res)
        res.data.isLoggedIn = true
        applySession(res.data)
      })
      .catch(err => {
        applySession({
          isLoggedIn: false
        })
      })
  }, [applySession])

  return (
    <StylesProvider injectFirst>
      <ThemeProvider theme={theme}>
        <Switch>
          <Route exact path="/" component={Home} />
          <Route exact path="/contacts" component={Contacts} />
          <Route exact path="/faq" component={Faq} />
          <Route exact path="/publicOfferAgreement" component={PublicOfferAgreement} />
          <Route exact path="/privacyPolicy" component={PrivacyPolicy} />
          <Route exact path="/about-us" component={AboutUs} />
          <Route exact path="/support" component={Support} />
          <Route exact path="/basket" component={Basket} />
          <Route exact path="/promotions" component={Promotions} />
          <Route exact path="/feedbacks" component={Feedbacks} />
          <Route
            exact
            path="/collections/:collection_url/:category_url"
            component={props => <Collection {...props} />}
          />
          <Route
            exact
            path="/products/:id"
            component={props => <Product {...props} />}
          />
        </Switch>
        <Footer />
      </ThemeProvider>
    </StylesProvider>
  )
}

const mapDispatchToProps = dispatch => {
  return {
    applySession: payload =>
      dispatch({ type: actions.APPLY_SESSION, payload: payload })
  }
}

export default connect(null, mapDispatchToProps)(App)
